import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiX, FiSearch } from 'react-icons/fi';

const SimonReceivablePage = () => {
    const [receivables, setReceivables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [supplierSummary, setSupplierSummary] = useState({
        Doraemon: { totalComms: 0, totalReceivable: 0, pendingPayable: 0, totalBonus: 0, totalBomb: 0 },
        Bean: { totalComms: 0, totalReceivable: 0, pendingPayable: 0, totalBonus: 0, totalBomb: 0 }
    });
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalReceivables, setTotalReceivables] = useState(0);
    const [selectedReceivable, setSelectedReceivable] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [currencyFilter, setCurrencyFilter] = useState('all');
    const [supplierFilter, setSupplierFilter] = useState('all');
    const [formData, setFormData] = useState({
        supplier: 'Bean',
        currency: 'SGD',
        amount: '',
        rate: '1.00',
        amountSGD: '',
        paymentDate: '',
        paymentForDateFrom: '',
        paymentForDateTo: '',
        remarks: ''
    });

    const currencies = ['SGD', 'LTC', 'USDT'];
    const suppliers = ['Bean', 'Doraemon'];

    const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

    const fetchSupplierSummary = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = {
                Authorization: `Bearer ${token}`,
                'ngrok-skip-browser-warning': 'true'
            };
            const [orderStatsResponse, receivableStatsResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/simonorders/stats`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/api/simonreceivables/stats`, { headers })
            ]);

            const commsBySupplier = orderStatsResponse.data?.data?.totalCommsBySupplier || {};
            const partnerComms = orderStatsResponse.data?.data?.supplierPartnerComms || {};
            const receivableBySupplierRows = receivableStatsResponse.data?.data?.totalBySupplier || [];

            const receivableBySupplier = { Doraemon: 0, Bean: 0 };
            for (const row of receivableBySupplierRows) {
                if (row?._id === 'Doraemon' || row?._id === 'Bean') {
                    receivableBySupplier[row._id] = Number(row.totalSGD ?? row.totalAmount ?? 0);
                }
            }

            const dP = partnerComms.Doraemon || {};
            const bP = partnerComms.Bean || {};
            setSupplierSummary({
                Doraemon: {
                    totalComms: Number(commsBySupplier.Doraemon || 0),
                    totalReceivable: Number(receivableBySupplier.Doraemon || 0),
                    pendingPayable: Number(commsBySupplier.Doraemon || 0) - Number(receivableBySupplier.Doraemon || 0),
                    totalBonus: Number(dP.totalBonus ?? 0),
                    totalBomb: Number(dP.totalBomb ?? 0)
                },
                Bean: {
                    totalComms: Number(commsBySupplier.Bean || 0),
                    totalReceivable: Number(receivableBySupplier.Bean || 0),
                    pendingPayable: Number(commsBySupplier.Bean || 0) - Number(receivableBySupplier.Bean || 0),
                    totalBonus: Number(bP.totalBonus ?? 0),
                    totalBomb: Number(bP.totalBomb ?? 0)
                }
            });
        } catch {
            // non-blocking
        }
    }, []);

    const fetchReceivables = useCallback(async (page, search = '', currency = 'all', supplier = 'all') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/simonreceivables?page=${page}&limit=20&sort=-paymentDate`;
            
            if (search) apiUrl += `&search=${search}`;
            if (currency !== 'all') apiUrl += `&currency=${currency}`;
            if (supplier !== 'all') apiUrl += `&supplier=${supplier}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setReceivables(response.data.data);
                setTotalPages(response.data.pagination.pages);
                setTotalReceivables(response.data.pagination.total);
            }
        } catch (err) {
            setError(`Failed to fetch Simon receivables: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSupplierSummary();
    }, [fetchSupplierSummary]);

    useEffect(() => {
        fetchReceivables(currentPage, searchTerm, currencyFilter, supplierFilter);
    }, [currentPage, searchTerm, currencyFilter, supplierFilter, fetchReceivables]);

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const submitData = {
                ...formData,
                amount: parseFloat(formData.amount),
                rate: parseFloat(formData.rate)
            };
            
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/simonreceivables`,
                submitData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            setShowCreateForm(false);
            setFormData({
                supplier: 'Bean',
                currency: 'SGD',
                amount: '',
                rate: '1.00',
                amountSGD: '',
                paymentDate: '',
                paymentForDateFrom: '',
                paymentForDateTo: '',
                remarks: ''
            });
            fetchReceivables(currentPage, searchTerm, currencyFilter, supplierFilter);
            fetchSupplierSummary();
        } catch (err) {
            setError(`Failed to create Simon receivable: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const submitData = {
                ...formData,
                amount: parseFloat(formData.amount),
                rate: parseFloat(formData.rate),
                amountSGD: formData.amountSGD ? parseFloat(formData.amountSGD) : (parseFloat(formData.amount) * parseFloat(formData.rate))
            };
            
            await axios.put(
                `${process.env.REACT_APP_API_URL}/api/simonreceivables/${selectedReceivable._id}`,
                submitData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            setShowEditModal(false);
            setSelectedReceivable(null);
            setFormData({
                supplier: 'Bean',
                currency: 'SGD',
                amount: '',
                rate: '1.00',
                amountSGD: '',
                paymentDate: '',
                paymentForDateFrom: '',
                paymentForDateTo: '',
                remarks: ''
            });
            fetchReceivables(currentPage, searchTerm, currencyFilter, supplierFilter);
            fetchSupplierSummary();
        } catch (err) {
            setError(`Failed to update Simon receivable: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleDelete = (receivable) => {
        setSelectedReceivable(receivable);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/simonreceivables/${selectedReceivable._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowDeleteConfirm(false);
            setSelectedReceivable(null);
            fetchReceivables(currentPage, searchTerm, currencyFilter, supplierFilter);
            fetchSupplierSummary();
        } catch (err) {
            setError(`Failed to delete Simon receivable: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleView = (receivable) => {
        setSelectedReceivable(receivable);
        setShowViewModal(true);
    };

    const handleEdit = (receivable) => {
        setSelectedReceivable(receivable);
        setFormData({
            supplier: receivable.supplier,
            currency: receivable.currency,
            amount: receivable.amount.toString(),
            rate: receivable.rate.toString(),
            amountSGD: receivable.amountSGD ? receivable.amountSGD.toString() : '',
            paymentDate: new Date(receivable.paymentDate).toISOString().split('T')[0],
            paymentForDateFrom: receivable.paymentForDateFrom ? new Date(receivable.paymentForDateFrom).toISOString().split('T')[0] : '',
            paymentForDateTo: receivable.paymentForDateTo ? new Date(receivable.paymentForDateTo).toISOString().split('T')[0] : '',
            remarks: receivable.remarks || ''
        });
        setShowEditModal(true);
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setCurrencyFilter('all');
        setSupplierFilter('all');
        setCurrentPage(1);
    };

    const handleCurrencyChange = (e) => {
        const currency = e.target.value;
        setFormData(prev => {
            const newRate = currency === 'SGD' ? '1.00' : prev.rate;
            const amount = parseFloat(prev.amount) || 0;
            const calculatedAmountSGD = amount * parseFloat(newRate);
            return {
                ...prev,
                currency,
                rate: newRate,
                amountSGD: calculatedAmountSGD > 0 ? calculatedAmountSGD.toFixed(2) : prev.amountSGD
            };
        });
    };

    const handleAmountChange = (e) => {
        const amount = e.target.value;
        setFormData(prev => {
            const amountNum = parseFloat(amount) || 0;
            const rateNum = parseFloat(prev.rate) || 0;
            const calculatedAmountSGD = amountNum * rateNum;
            return {
                ...prev,
                amount,
                amountSGD: calculatedAmountSGD > 0 ? calculatedAmountSGD.toFixed(2) : ''
            };
        });
    };

    const handleRateChange = (e) => {
        const rate = e.target.value;
        setFormData(prev => {
            const amountNum = parseFloat(prev.amount) || 0;
            const rateNum = parseFloat(rate) || 0;
            const calculatedAmountSGD = amountNum * rateNum;
            return {
                ...prev,
                rate,
                amountSGD: calculatedAmountSGD > 0 ? calculatedAmountSGD.toFixed(2) : prev.amountSGD
            };
        });
    };

    const handleAmountSGDChange = (e) => {
        const amountSGD = e.target.value;
        setFormData(prev => {
            const amountSGDNum = parseFloat(amountSGD) || 0;
            const amountNum = parseFloat(prev.amount) || 0;
            if (amountNum > 0) {
                const calculatedRate = amountSGDNum / amountNum;
                return {
                    ...prev,
                    amountSGD,
                    rate: calculatedRate > 0 ? calculatedRate.toFixed(2) : prev.rate
                };
            }
            return {
                ...prev,
                amountSGD
            };
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-SG');
    };

    const formatAmount = (amount, decimals = 2) => {
        if (!amount && amount !== 0) return 'N/A';
        return `$${amount.toFixed(decimals)}`;
    };

    const getCurrencyColor = (currency) => {
        switch (currency) {
            case 'USDT':
                return 'bg-green-100 text-green-800';
            case 'SGD':
                return 'bg-blue-100 text-blue-800';
            case 'LTC':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getSupplierColor = (supplier) => {
        switch (supplier) {
            case 'Bean':
                return 'bg-yellow-100 text-yellow-800';
            case 'Doraemon':
                return 'bg-blue-100 text-blue-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-blue-900 mb-2">Doraemon</h3>
                        <div className="text-sm space-y-1">
                            <div><span className="font-semibold text-gray-700">Total Comms:</span> <span className="text-blue-700">{formatMoney(supplierSummary.Doraemon.totalComms)}</span></div>
                            <div><span className="font-semibold text-gray-700">Total Receivable (SGD):</span> <span className="text-blue-700">{formatMoney(supplierSummary.Doraemon.totalReceivable)}</span></div>
                            <div><span className="font-semibold text-gray-700">Pending Payable:</span> <span className="text-green-700 font-semibold">{formatMoney(supplierSummary.Doraemon.pendingPayable)}</span></div>
                            <div><span className="font-semibold text-gray-700">Credit:</span> <span className="text-emerald-700">{formatMoney(supplierSummary.Doraemon.totalBonus)}</span> <span className="text-gray-500 text-xs">(SUPDORAEMON · credit orders)</span></div>
                            <div><span className="font-semibold text-gray-700">Debit:</span> <span className="text-red-700">{formatMoney(supplierSummary.Doraemon.totalBomb)}</span> <span className="text-gray-500 text-xs">(SUPDORAEMON · debit orders)</span></div>
                            <div><span className="font-semibold text-gray-700">Total Bonus:</span>{' '}
                                <span className={Number(supplierSummary.Doraemon.totalBonus || 0) + Number(supplierSummary.Doraemon.totalBomb || 0) >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                                    {formatMoney(Number(supplierSummary.Doraemon.totalBonus || 0) + Number(supplierSummary.Doraemon.totalBomb || 0))}
                                </span>{' '}
                                <span className="text-gray-500 text-xs">(Credit + Debit)</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-green-50 border border-green-100 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-green-900 mb-2">Bean</h3>
                        <div className="text-sm space-y-1">
                            <div><span className="font-semibold text-gray-700">Total Comms:</span> <span className="text-blue-700">{formatMoney(supplierSummary.Bean.totalComms)}</span></div>
                            <div><span className="font-semibold text-gray-700">Total Receivable (SGD):</span> <span className="text-blue-700">{formatMoney(supplierSummary.Bean.totalReceivable)}</span></div>
                            <div><span className="font-semibold text-gray-700">Pending Payable:</span> <span className="text-green-700 font-semibold">{formatMoney(supplierSummary.Bean.pendingPayable)}</span></div>
                            <div><span className="font-semibold text-gray-700">Credit:</span> <span className="text-emerald-700">{formatMoney(supplierSummary.Bean.totalBonus)}</span> <span className="text-gray-500 text-xs">(SUPBEAN · credit orders)</span></div>
                            <div><span className="font-semibold text-gray-700">Debit:</span> <span className="text-red-700">{formatMoney(supplierSummary.Bean.totalBomb)}</span> <span className="text-gray-500 text-xs">(SUPBEAN · debit orders)</span></div>
                            <div><span className="font-semibold text-gray-700">Total Bonus:</span>{' '}
                                <span className={Number(supplierSummary.Bean.totalBonus || 0) + Number(supplierSummary.Bean.totalBomb || 0) >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                                    {formatMoney(Number(supplierSummary.Bean.totalBonus || 0) + Number(supplierSummary.Bean.totalBomb || 0))}
                                </span>{' '}
                                <span className="text-gray-500 text-xs">(Credit + Debit)</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Simon Receivables</h1>
                        <p className="text-gray-600">Total: {totalReceivables} receivables</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create New Receivable
                    </button>
                </div>

                {/* Search and Filter Bar */}
                <div className="mb-6">
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                        placeholder="Search by supplier or remarks..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <FiSearch className="h-5 w-5 text-gray-400" />
                                    </div>
                                </div>
                                <button
                                    onClick={handleSearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                                >
                                    <FiSearch className="mr-2" />
                                    Search
                                </button>
                                <button
                                    onClick={handleClear}
                                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div>
                            <select
                                value={currencyFilter}
                                onChange={(e) => {
                                    setCurrencyFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Currencies</option>
                                {currencies.map(curr => (
                                    <option key={curr} value={curr}>{curr}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <select
                                value={supplierFilter}
                                onChange={(e) => {
                                    setSupplierFilter(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Suppliers</option>
                                {suppliers.map(sup => (
                                    <option key={sup} value={sup}>{sup}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {/* Create Form Modal */}
                {showCreateForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <h2 className="text-xl font-bold mb-4">Create New Simon Receivable</h2>
                            <form onSubmit={handleCreateSubmit}>
                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Supplier *
                                        </label>
                                        <select
                                            required
                                            value={formData.supplier}
                                            onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            {suppliers.map(sup => (
                                                <option key={sup} value={sup}>{sup}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Currency *
                                        </label>
                                        <select
                                            required
                                            value={formData.currency}
                                            onChange={handleCurrencyChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            {currencies.map(curr => (
                                                <option key={curr} value={curr}>{curr}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            required
                                            value={formData.amount}
                                            onChange={handleAmountChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Rate * {formData.currency === 'SGD' && '(Auto-set to 1.00 for SGD)'}
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.rate}
                                            onChange={handleRateChange}
                                            disabled={formData.currency === 'SGD'}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount in SGD
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={formData.amountSGD}
                                            onChange={handleAmountSGDChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-blue-50 text-blue-700 font-bold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Date *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.paymentDate}
                                            onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment For Date From
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.paymentForDateFrom}
                                            onChange={(e) => setFormData({ ...formData, paymentForDateFrom: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment For Date To
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.paymentForDateTo}
                                            onChange={(e) => setFormData({ ...formData, paymentForDateTo: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Remarks
                                        </label>
                                        <textarea
                                            rows="2"
                                            value={formData.remarks}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="Optional remarks..."
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowCreateForm(false);
                                            setFormData({
                                                supplier: 'Bean',
                                                currency: 'SGD',
                                                amount: '',
                                                rate: '1.00',
                                                paymentDate: '',
                                                paymentForDateFrom: '',
                                                paymentForDateTo: '',
                                                remarks: ''
                                            });
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Create
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Edit Form Modal */}
                {showEditModal && selectedReceivable && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                            <h2 className="text-xl font-bold mb-4">Edit Simon Receivable</h2>
                            <form onSubmit={handleEditSubmit}>
                                <div className="grid grid-cols-1 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Supplier *
                                        </label>
                                        <select
                                            required
                                            value={formData.supplier}
                                            onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            {suppliers.map(sup => (
                                                <option key={sup} value={sup}>{sup}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Currency *
                                        </label>
                                        <select
                                            required
                                            value={formData.currency}
                                            onChange={handleCurrencyChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        >
                                            {currencies.map(curr => (
                                                <option key={curr} value={curr}>{curr}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            required
                                            value={formData.amount}
                                            onChange={handleAmountChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Rate * {formData.currency === 'SGD' && '(Auto-set to 1.00 for SGD)'}
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            required
                                            value={formData.rate}
                                            onChange={handleRateChange}
                                            disabled={formData.currency === 'SGD'}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:bg-gray-100"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Amount in SGD
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={formData.amountSGD}
                                            onChange={handleAmountSGDChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-blue-50 text-blue-700 font-bold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment Date *
                                        </label>
                                        <input
                                            type="date"
                                            required
                                            value={formData.paymentDate}
                                            onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment For Date From
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.paymentForDateFrom}
                                            onChange={(e) => setFormData({ ...formData, paymentForDateFrom: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Payment For Date To
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.paymentForDateTo}
                                            onChange={(e) => setFormData({ ...formData, paymentForDateTo: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Remarks
                                        </label>
                                        <textarea
                                            rows="2"
                                            value={formData.remarks}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                            placeholder="Optional remarks..."
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowEditModal(false);
                                            setSelectedReceivable(null);
                                            setFormData({
                                                supplier: 'Bean',
                                                currency: 'SGD',
                                                amount: '',
                                                rate: '1.00',
                                                amountSGD: '',
                                                paymentDate: '',
                                                paymentForDateFrom: '',
                                                paymentForDateTo: '',
                                                remarks: ''
                                            });
                                        }}
                                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Update
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* View Modal */}
                {showViewModal && selectedReceivable && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold">Simon Receivable Details</h2>
                                <button onClick={() => { setShowViewModal(false); setSelectedReceivable(null); }}>
                                    <FiX className="w-6 h-6" />
                                </button>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <label className="font-semibold">Supplier:</label>
                                    <span className={`ml-2 px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getSupplierColor(selectedReceivable.supplier)}`}>
                                        {selectedReceivable.supplier}
                                    </span>
                                </div>
                                <div>
                                    <label className="font-semibold">Currency:</label>
                                    <span className={`ml-2 px-2 py-1 text-xs leading-5 font-semibold rounded-full ${getCurrencyColor(selectedReceivable.currency)}`}>
                                        {selectedReceivable.currency}
                                    </span>
                                </div>
                                <div>
                                    <label className="font-semibold">Amount:</label>
                                    <p className="text-gray-700">{formatAmount(selectedReceivable.amount, 3)} {selectedReceivable.currency}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Rate:</label>
                                    <p className="text-gray-700">{selectedReceivable.rate}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Amount (SGD):</label>
                                    <p className="text-gray-700">{formatAmount(selectedReceivable.amountSGD)} SGD</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Payment Date:</label>
                                    <p className="text-gray-700">{formatDate(selectedReceivable.paymentDate)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Payment For Date From:</label>
                                    <p className="text-gray-700">{formatDate(selectedReceivable.paymentForDateFrom)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Payment For Date To:</label>
                                    <p className="text-gray-700">{formatDate(selectedReceivable.paymentForDateTo)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Remarks:</label>
                                    <p className="text-gray-700">{selectedReceivable.remarks || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Created:</label>
                                    <p className="text-gray-700">{formatDate(selectedReceivable.createdAt)}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Updated:</label>
                                    <p className="text-gray-700">{formatDate(selectedReceivable.updatedAt)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && selectedReceivable && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                            <p className="mb-6">Are you sure you want to delete this receivable?</p>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => { setShowDeleteConfirm(false); setSelectedReceivable(null); }}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Supplier
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount (SGD)
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment Date
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Payment For Period
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : receivables.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-4 text-center">
                                        No Simon receivables found
                                    </td>
                                </tr>
                            ) : (
                                receivables.map((receivable) => (
                                    <tr key={receivable._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getSupplierColor(receivable.supplier)}`}>
                                                {receivable.supplier}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-700">
                                            {formatAmount(receivable.amountSGD)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {formatDate(receivable.paymentDate)}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {receivable.paymentForDateFrom && receivable.paymentForDateTo ? (
                                                <div>
                                                    <div className="text-gray-900">{formatDate(receivable.paymentForDateFrom)}</div>
                                                    <div className="text-gray-500 text-xs">to {formatDate(receivable.paymentForDateTo)}</div>
                                                </div>
                                            ) : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button
                                                onClick={() => handleView(receivable)}
                                                className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                title="View Details"
                                            >
                                                <FiEye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(receivable)}
                                                className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                title="Edit Receivable"
                                            >
                                                <FiEdit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(receivable)}
                                                className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                title="Delete Receivable"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        </Layout>
    );
};

export default SimonReceivablePage;
